import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Component, computed, inject, signal } from '@angular/core';
import { NonNullableFormBuilder, Validators } from '@angular/forms';
import { OuiButton } from 'omnium-ui/button';
import { OuiDialogModule, OuiDialogService } from 'omnium-ui/dialog';
import { OuiCheckbox, OuiTextarea } from 'omnium-ui/form-field';
import { OuiIconModule } from 'omnium-ui/icon';
import { OuiLoadingModule } from 'omnium-ui/loading';
import { OuiSnackbarService } from 'omnium-ui/snackbar';
import { ResiliationConsultantService } from '../services/resiliation-consultant.service';
import { DemandeResiliationModel } from '../models/demande-resiliation.model';
import { ConfirmationDemandeResiliationDialogComponent } from '../confirmation-demande-resiliation-dialog/confirmation-demande-resiliation-dialog.component';
import moment from 'moment';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-demande-resiliation-consultant-dialog',
  standalone: true,
  imports: [
    OuiDialogModule,
    OuiButton,
    OuiIconModule,
    OuiTextarea,
    OuiCheckbox,
    OuiLoadingModule,
  ],
  templateUrl: './demande-resiliation-consultant-dialog.component.html',
  styleUrl: './demande-resiliation-consultant-dialog.component.scss',
})
export class DemandeResiliationConsultantDialogComponent {
  private readonly resiliationService = inject(ResiliationConsultantService);
  private readonly snackbarService = inject(OuiSnackbarService);
  private readonly dialogService = inject(OuiDialogService);

  readonly codeConsultant: string = inject(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(
    MatDialogRef<DemandeResiliationConsultantDialogComponent>
  );

  readonly isLoading = signal<boolean>(false);

  readonly form;

  readonly displayPreavis = signal<boolean>(false);
  readonly nbMoisPreavis = signal<number>(0);

  readonly dateFinPreavis = computed<string>(() => {
    const nbMoisPreavis = this.nbMoisPreavis();

    if (nbMoisPreavis === 0) return '';

    return moment().add(nbMoisPreavis, 'months').format('DD/MM/YYYY');
  });

  constructor(fb: NonNullableFormBuilder) {
    this.form = fb.group({
      commentaire: fb.control<string>('', [Validators.required]),
      sansPreavis: fb.control<boolean>(true),
    });

    this.resiliationService
      .getResiliationPreavis()
      .then(value => this.nbMoisPreavis.set(value));

    this.form
      .get('sansPreavis')
      ?.valueChanges.pipe(takeUntilDestroyed())
      .subscribe(v => this.displayPreavis.set(!v));
  }

  async sendDemandeResiliation() {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isLoading.set(true);
    try {
      const value = this.form.value;

      const model: DemandeResiliationModel = {
        commentaire: value.commentaire!,
        preavis: !value.sansPreavis!,
      };

      await this.resiliationService.sendDemandeResiliation(model);

      this.dialogRef.close();

      this.dialogService.openDialog(ConfirmationDemandeResiliationDialogComponent);
    } catch (error) {
      console.error(error);
      this.snackbarService.open(
        "Une erreur est survenue durant l'envoi de votre demande de résiliation",
        'error'
      );
    }
    this.isLoading.set(false);
  }
}
